import React from "react";
import { Box, Stack, Typography, Button, Chip, CircularProgress } from "@mui/material";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import AttachFileRoundedIcon from "@mui/icons-material/AttachFileRounded";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";
import {
  PILL_BUTTON_BG,
  pillButtonBaseSx,
  pillButtonInteractiveSx,
  withPillButtonClassName,
} from "../../../utils/pillButtonStyles";

const PURPLE = PILL_BUTTON_BG;
const PURPLE_HOVER = PILL_BUTTON_BG;
const SOFT_BG = "#F7F5FF";
const SOFT_BORDER = "#EAE5FF";
const RAW_API_BASE = (process.env.REACT_APP_API_BASE || "").trim();
const RESUME_ACCEPT =
  ".pdf,.doc,.docx,.rtf,.txt,.odt,.jpg,.jpeg,.png,.webp,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/rtf,text/rtf,text/plain,application/vnd.oasis.opendocument.text,image/jpeg,image/png,image/webp";

function PillButton({ sx, ...props }) {
  return (
    <Button
      {...props}
      className={withPillButtonClassName(props.className)}
      sx={[pillButtonBaseSx, pillButtonInteractiveSx, { fontWeight: 400, px: 2.1 }, sx || {}]}
    />
  );
}

function getResumeExtension(value = "") {
  const match = String(value || "")
    .toLowerCase()
    .match(/\.([a-z0-9]+)$/i);
  return match?.[1] || "";
}

function safeTrim(value) {
  if (value === undefined || value === null) return "";
  return String(value).trim();
}

function buildWindowOrigin() {
  if (typeof window === "undefined" || !window.location) return "";
  return `${window.location.protocol}//${window.location.host}`.replace(/\/$/, "");
}

function buildPublicBase() {
  const raw = safeTrim(RAW_API_BASE);
  const windowOrigin = buildWindowOrigin();

  if (!raw) return windowOrigin;

  const base = raw.replace(/\/$/, "");

  if (base === "/api") return windowOrigin;

  if (/^https?:\/\//i.test(base) && base.endsWith("/api")) {
    return base.slice(0, -4);
  }

  if (/^https?:\/\//i.test(base)) {
    return base;
  }

  if (base.endsWith("/api")) {
    return `${windowOrigin}${base.slice(0, -4)}`.replace(/\/$/, "");
  }

  return `${windowOrigin}${base}`.replace(/\/$/, "");
}

function joinUrl(base, pathname) {
  const normalizedBase = (base || "").replace(/\/$/, "");
  const normalizedPath = (pathname || "").startsWith("/")
    ? pathname
    : `/${pathname || ""}`;
  return normalizedBase ? `${normalizedBase}${normalizedPath}` : normalizedPath;
}

function makeAbsolutePublicFileUrl(urlOrPath) {
  const raw = safeTrim(urlOrPath);
  if (!raw) return "";

  const publicBase = buildPublicBase();

  if (raw.startsWith("//")) {
    if (typeof window !== "undefined" && window.location?.protocol) {
      return `${window.location.protocol}${raw}`;
    }
    return `https:${raw}`;
  }

  try {
    const parsed = new URL(raw);
    const host = safeTrim(parsed.hostname).toLowerCase();

    if (host === "localhost" || host === "127.0.0.1" || host === "0.0.0.0") {
      const rebuiltPath = `${parsed.pathname || ""}${parsed.search || ""}${parsed.hash || ""}`;
      return joinUrl(publicBase, rebuiltPath);
    }

    return parsed.toString();
  } catch {
    // continue
  }

  if (raw.startsWith("/")) {
    return joinUrl(publicBase, raw);
  }

  if (raw.startsWith("uploads/")) {
    return joinUrl(publicBase, `/${raw}`);
  }

  if (raw.includes("/uploads/")) {
    const uploadsIndex = raw.indexOf("/uploads/");
    return joinUrl(publicBase, raw.slice(uploadsIndex));
  }

  if (raw.startsWith("candidate_resumes/")) {
    return joinUrl(publicBase, `/uploads/${raw}`);
  }

  return joinUrl(publicBase, `/${raw.replace(/^\/+/, "")}`);
}

function getResumeFilename(value = "") {
  const raw = safeTrim(value);
  if (!raw) return "Resume";

  const withoutQuery = raw.split(/[?#]/)[0] || raw;
  const lastSegment = withoutQuery.split("/").pop() || withoutQuery;

  try {
    return decodeURIComponent(lastSegment) || "Resume";
  } catch {
    return lastSegment || "Resume";
  }
}

function isPdfResumeFile(file = null) {
  const name = String(file?.name || "").toLowerCase();
  const type = String(file?.type || "").toLowerCase();
  return type.includes("pdf") || name.endsWith(".pdf");
}

function UploadTile({ onPick, accept, disabled, parsingResume }) {
  return (
    <Box
      sx={{
        border: `1px solid ${SOFT_BORDER}`,
        bgcolor: SOFT_BG,
        borderRadius: 2.5,
        p: 1.5,
      }}
    >
      <Stack direction="row" spacing={1.25} alignItems="center">
        <Box
          sx={{
            width: 34,
            height: 34,
            borderRadius: 2,
            display: "grid",
            placeItems: "center",
            bgcolor: "#fff",
            border: `1px solid ${SOFT_BORDER}`,
          }}
        >
          <UploadFileIcon sx={{ fontSize: 18, color: PURPLE }} />
        </Box>

        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontSize: 12.5, fontWeight: 900, color: "#111827" }}>
            Resume
          </Typography>
          <Typography sx={{ fontSize: 11.5, color: "#6B7280", mt: 0.25 }}>
            Upload PDF/DOC/DOCX. Will auto-fill fields and parse experience/education.
          </Typography>
        </Box>

        <PillButton
          component="label"
          variant="contained"
          disabled={disabled}
          sx={{ bgcolor: PURPLE, "&:hover": { bgcolor: PURPLE_HOVER } }}
          startIcon={<AttachFileRoundedIcon sx={{ fontSize: 16 }} />}
        >
          {parsingResume ? "Parsing..." : "Resume"}
          <input hidden type="file" accept={accept} onChange={onPick} />
        </PillButton>
      </Stack>
    </Box>
  );
}

function ResumePreview({ resumeFile, previewUrl }) {
  if (!(resumeFile instanceof File) || !previewUrl) return null;

  const extension = getResumeExtension(resumeFile?.name || "");
  const isPdf = isPdfResumeFile(resumeFile);

  return (
    <Box
      sx={{
        mt: 1.2,
        border: `1px solid ${SOFT_BORDER}`,
        borderRadius: 2.5,
        overflow: "hidden",
        bgcolor: "#fff",
      }}
    >
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        sx={{
          px: 1.5,
          py: 1.2,
          borderBottom: `1px solid ${SOFT_BORDER}`,
          flexWrap: "wrap",
        }}
      >
        <DescriptionOutlinedIcon sx={{ fontSize: 18, color: PURPLE }} />

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography noWrap sx={{ fontSize: 12.5, fontWeight: 900, color: "#111827" }}>
            {resumeFile?.name || "Resume"}
          </Typography>
          <Typography sx={{ fontSize: 11.5, color: "#6B7280" }}>
            {extension ? `.${extension.toUpperCase()} resume attached` : "Resume attached"}
          </Typography>
        </Box>

        <Button
          component="a"
          href={previewUrl}
          target="_blank"
          rel="noreferrer"
          size="small"
          startIcon={<OpenInNewRoundedIcon sx={{ fontSize: 16 }} />}
          sx={{ textTransform: "none", fontWeight: 800, color: PURPLE }}
        >
          Open
        </Button>
      </Stack>

      {isPdf ? (
        <Box sx={{ height: 320, bgcolor: "#fff" }}>
          <iframe
            title={resumeFile?.name || "Resume preview"}
            src={`${previewUrl}#toolbar=0&navpanes=0&view=FitH`}
            style={{
              width: "100%",
              height: "100%",
              border: 0,
              display: "block",
              background: "#fff",
            }}
          />
        </Box>
      ) : (
        <Box
          sx={{
            px: 2,
            py: 2.25,
            textAlign: "center",
            bgcolor: "#fff",
          }}
        >
          <DescriptionOutlinedIcon sx={{ fontSize: 28, color: PURPLE, mb: 0.75 }} />
          <Typography sx={{ fontSize: 12.5, fontWeight: 900, color: "#111827" }}>
            Preview is not available for this file type
          </Typography>
          <Typography sx={{ fontSize: 11.5, color: "#6B7280", mt: 0.4 }}>
            The uploaded resume is attached and will still be saved with the candidate.
          </Typography>
        </Box>
      )}
    </Box>
  );
}

function ExistingResumePreview({ resumeUrl }) {
  const previewUrl = makeAbsolutePublicFileUrl(resumeUrl);
  if (!previewUrl) return null;

  const filename = getResumeFilename(resumeUrl);

  return (
    <Box
      sx={{
        mt: 1.2,
        border: `1px solid ${SOFT_BORDER}`,
        borderRadius: 2.5,
        overflow: "hidden",
        bgcolor: "#fff",
      }}
    >
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        sx={{
          px: 1.5,
          py: 1.2,
          borderBottom: `1px solid ${SOFT_BORDER}`,
          flexWrap: "wrap",
        }}
      >
        <DescriptionOutlinedIcon sx={{ fontSize: 18, color: PURPLE }} />

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography noWrap sx={{ fontSize: 12.5, fontWeight: 900, color: "#111827" }}>
            {filename}
          </Typography>
          <Typography sx={{ fontSize: 11.5, color: "#6B7280" }}>
            Existing resume on this candidate
          </Typography>
        </Box>

        <Button
          component="a"
          href={previewUrl}
          target="_blank"
          rel="noreferrer"
          size="small"
          startIcon={<OpenInNewRoundedIcon sx={{ fontSize: 16 }} />}
          sx={{ textTransform: "none", fontWeight: 800, color: PURPLE }}
        >
          Open
        </Button>
      </Stack>

      <Box
        sx={{
          px: 1.5,
          py: 1.15,
          bgcolor: "#fff",
          borderTop: `1px solid ${SOFT_BORDER}`,
        }}
      >
        <Typography sx={{ fontSize: 11.5, color: "#6B7280" }}>
          This candidate already has a saved resume. You can keep it and still upload a new one below if needed.
        </Typography>
      </Box>
    </Box>
  );
}

function getTaskBadgeProps(status) {
  if (status === "done") {
    return {
      label: "Done",
      sx: {
        bgcolor: "#ECFDF3",
        color: "#027A48",
        fontWeight: 700,
      },
    };
  }

  if (status === "working") {
    return {
      label: "Working",
      sx: {
        bgcolor: "#EEF2FF",
        color: "#3451F4",
        fontWeight: 700,
      },
    };
  }

  return {
    label: "Queued",
    sx: {
      bgcolor: "#F5F3FF",
      color: PURPLE,
      fontWeight: 700,
    },
  };
}

function TaskStatusIcon({ status }) {
  if (status === "working") {
    return <CircularProgress size={14} thickness={5} sx={{ color: PURPLE }} />;
  }

  return (
    <Box
      sx={{
        width: 14,
        height: 14,
        borderRadius: 999,
        bgcolor: status === "done" ? "#22C55E" : "#C084FC",
        boxShadow:
          status === "done"
            ? "0 0 0 3px rgba(34,197,94,0.12)"
            : "0 0 0 3px rgba(124,58,237,0.12)",
        flexShrink: 0,
      }}
    />
  );
}

function BackgroundTasksPanel({ title, tasks, note }) {
  const safeTasks = Array.isArray(tasks) ? tasks.filter(Boolean) : [];
  if (!safeTasks.length) return null;

  return (
    <Box
      sx={{
        mt: 1.3,
        border: `1px solid ${SOFT_BORDER}`,
        borderRadius: 2.5,
        bgcolor: "#FFFCF5",
        px: 1.5,
        py: 1.35,
      }}
    >
      <Typography sx={{ fontSize: 12.5, fontWeight: 900, color: "#111827", mb: 0.45 }}>
        Background Tasks
      </Typography>
      <Typography sx={{ fontSize: 11.5, color: "#6B7280", mb: 1.1 }}>
        {title}
      </Typography>

      <Stack spacing={0.9}>
        {safeTasks.map((task) => {
          const badge = getTaskBadgeProps(task.status);
          return (
            <Stack
              key={task.key || task.label}
              direction="row"
              spacing={1}
              alignItems="center"
              justifyContent="space-between"
            >
              <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0, flex: 1 }}>
                <TaskStatusIcon status={task.status} />
                <Typography sx={{ fontSize: 11.75, color: "#111827", minWidth: 0 }}>
                  {task.label}
                </Typography>
              </Stack>

              <Chip
                size="small"
                label={badge.label}
                sx={[
                  {
                    height: 22,
                    "& .MuiChip-label": {
                      px: 1,
                    },
                  },
                  badge.sx,
                ]}
              />
            </Stack>
          );
        })}
      </Stack>

      {note ? (
        <Typography sx={{ fontSize: 11.25, color: "#6B7280", mt: 1.15 }}>
          {note}
        </Typography>
      ) : null}
    </Box>
  );
}

export default function UploadCandidateStep({
  resumeFile,
  resumeAiData,
  existingResumeUrl = "",
  backgroundTasks = [],
  backgroundTasksTitle = "",
  backgroundTasksVisible = false,
  backgroundTasksNote = "",
  parsingResume,
  saving,
  onPickResume,
  onClearResume,
}) {
  const [previewUrl, setPreviewUrl] = React.useState("");

  React.useEffect(() => {
    if (!(resumeFile instanceof File)) {
      setPreviewUrl("");
      return undefined;
    }

    const nextPreviewUrl = URL.createObjectURL(resumeFile);
    setPreviewUrl(nextPreviewUrl);

    return () => {
      URL.revokeObjectURL(nextPreviewUrl);
    };
  }, [resumeFile]);

  return (
    <Box sx={{ p: 4 }}>
      <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: "#6B7280", mb: 0.6 }}>
        Resume
      </Typography>

      <UploadTile
        onPick={onPickResume}
        accept={RESUME_ACCEPT}
        disabled={saving || parsingResume}
        parsingResume={parsingResume}
      />

      <ExistingResumePreview resumeUrl={existingResumeUrl} />

      <ResumePreview resumeFile={resumeFile} previewUrl={previewUrl} />

      {backgroundTasksVisible ? (
        <BackgroundTasksPanel
          title={backgroundTasksTitle}
          tasks={backgroundTasks}
          note={backgroundTasksNote}
        />
      ) : null}

      <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1.2 }} flexWrap="wrap">
        {parsingResume && <CircularProgress size={16} />}

        {resumeFile ? (
          <>
            <Chip size="small" label={resumeFile.name} />
            {!!resumeAiData?.experience_items?.length && (
              <Chip
                size="small"
                color="secondary"
                label={`Experience: ${resumeAiData.experience_items.length}`}
              />
            )}
            {!!resumeAiData?.education_items?.length && (
              <Chip
                size="small"
                color="secondary"
                label={`Education: ${resumeAiData.education_items.length}`}
              />
            )}
            <Button
              size="small"
              onClick={onClearResume}
              disabled={saving || parsingResume}
              sx={{ textTransform: "none", fontWeight: 900, color: PURPLE }}
            >
              Remove
            </Button>
          </>
        ) : (
          <Typography sx={{ fontSize: 11.5, color: "#6B7280" }}>PDF / DOC / DOCX</Typography>
        )}
      </Stack>
    </Box>
  );
}
